import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { assessmentService } from '../services/assessmentService';
import { questionService } from '../services/questionService';
import { maturityScoringService } from '../services/maturityScoringService';
import { useHeader } from '../contexts/HeaderContext';
import LoadingSpinner from '../components/LoadingSpinner';
import AssessmentNotes from '../components/AssessmentNotes';
import AssessmentActionHeader from '../components/AssessmentActionHeader';
import {
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface MaturityAnalysis {
  overallScore: number;
  overallPercentage: number;
  dimensionScores: Array<{
    dimension: string;
    score: number;
    maxScore: number;
    percentage: number;
    answeredCount: number;
    questionCount: number;
  }>;
  strengths: string[];
  weaknesses: string[];
  recommendations: string[];
}

const AssessmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const assessmentId = parseInt(id || '0');
  const [maturityAnalysis, setMaturityAnalysis] = useState<MaturityAnalysis | null>(null);
  const { setTitle } = useHeader();

  const { data: assessment, isLoading, error, refetch } = useQuery({
    queryKey: ['assessment', assessmentId],
    queryFn: () => assessmentService.getAssessment(assessmentId),
    enabled: !!assessmentId,
  });

  // Load maturity analysis when assessment is loaded
  useEffect(() => {
    const loadMaturityAnalysis = async () => {
      if (!assessment) return;

      if (assessment.client) {
        setTitle(`${assessment.client.name} Evaluation`);
      }

      try {
        const [questionsData, responsesData] = await Promise.all([
          questionService.getQuestions({
            questionnaireLabel: assessment.questionnaireLabel || undefined,
            questionnaireVersion: assessment.questionnaireVersion || undefined
          }),
          assessmentService.getAssessmentResponses(assessmentId)
        ]);

        const rawAnalysis = maturityScoringService.calculateMaturityScores(
          assessment,
          questionsData,
          responsesData
        );

        const totalScore = rawAnalysis.dimensionScores.reduce((acc, d) => acc + d.score, 0);
        const maxScore = rawAnalysis.dimensionScores.reduce((acc, d) => acc + d.maxScore, 0);

        const analysis: MaturityAnalysis = {
          ...rawAnalysis,
          overallScore: totalScore,
          overallPercentage: maxScore > 0 ? (totalScore / maxScore) * 100 : 0
        };

        setMaturityAnalysis(analysis);

      } catch (error: any) {
        console.error('Failed to load maturity analysis:', error);
      }
    };

    loadMaturityAnalysis();
  }, [assessment, assessmentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Error Loading Evaluation</h3>
        <p className="mt-1 text-sm text-gray-500">{(error as Error).message}</p>
        <Link to="/assessments" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-500">
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Evaluations
        </Link>
      </div>
    );
  }

  if (!assessment) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">Evaluation Not Found</h3>
        <p className="mt-1 text-sm text-gray-500">The requested evaluation could not be found.</p>
        <Link to="/assessments" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-500">
          <ArrowLeftIcon className="w-4 h-4 mr-2" />
          Back to Evaluations
        </Link>
      </div>
    );
  }


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <div className="flex items-center">
        <Link
          to="/assessments"
          className="inline-flex items-center text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to Evaluations
        </Link>
      </div>

      {/* Action Header */}
      <AssessmentActionHeader
        assessment={assessment}
        maturityAnalysis={maturityAnalysis}
        onAddNotes={() => {
          // Scroll to notes section
          const notesElement = document.querySelector('[data-notes-section]');
          if (notesElement) {
            notesElement.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        onStatusChange={refetch}
      />

      {/* Assessment Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Client Information */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Client Information</h3>
            </div>
            <div className="card-body">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Client Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{assessment.client?.name}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Industry</dt>
                  <dd className="mt-1 text-sm text-gray-900">{assessment.client?.industry || 'Not specified'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{assessment.client?.email || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Company Size</dt>
                  <dd className="mt-1 text-sm text-gray-900">{assessment.client?.companySize || 'Not specified'}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Goals */}
          {assessment.goals && (Array.isArray(assessment.goals) ? assessment.goals.length > 0 : Object.keys(assessment.goals).length > 0) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">Evaluation Goals</h3>
              </div>
              <div className="card-body">
                <dl className="space-y-4">
                  {Array.isArray(assessment.goals) ? (
                    assessment.goals.map((goal: any, index: number) => (
                      <div key={goal.id || index}>
                        <dt className="text-sm font-medium text-gray-500 capitalize flex items-center">
                          Goal {index + 1}
                          {goal.priority && (
                            <span className="ml-2 text-xs text-gray-400 font-normal">
                              ({goal.priority})
                            </span>
                          )}
                        </dt>
                        <dd className="mt-1 text-sm text-gray-900">{goal.text || String(goal)}</dd>
                      </div>
                    ))
                  ) : (
                    Object.entries(assessment.goals).map(([key, value]) => (
                      <div key={key}>
                        <dt className="text-sm font-medium text-gray-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</dt>
                        <dd className="mt-1 text-sm text-gray-900">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                      </div>
                    ))
                  )}
                </dl>
              </div>
            </div>
          )}

          {/* AI Insights */}
          {assessment.aiInsights && Object.keys(assessment.aiInsights).length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-medium text-gray-900">AI Insights</h3>
              </div>
              <div className="card-body">
                {assessment.aiInsights.strengths && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-green-700 mb-2">Strengths</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      {assessment.aiInsights.strengths.map((strength: string, index: number) => (
                        <li key={index}>{strength}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {assessment.aiInsights.improvements && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-yellow-700 mb-2">Areas for Improvement</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      {assessment.aiInsights.improvements.map((improvement: string, index: number) => (
                        <li key={index}>{improvement}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {assessment.aiInsights.recommendations && (
                  <div>
                    <h4 className="text-sm font-medium text-blue-700 mb-2">Recommendations</h4>
                    <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                      {assessment.aiInsights.recommendations.map((recommendation: string, index: number) => (
                        <li key={index}>{recommendation}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">

          {/* Assessment Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Evaluation Info</h3>
            </div>
            <div className="card-body">
              <dl className="space-y-3">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(assessment.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(assessment.updatedAt)}</dd>
                </div>
                {assessment.completedAt && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Completed</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(assessment.completedAt)}</dd>
                  </div>
                )}
                <div>
                  <dt className="text-sm font-medium text-gray-500">Advisor</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {assessment.advisor?.firstName} {assessment.advisor?.lastName}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Completion Status */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Completion Status</h3>
            </div>
            <div className="card-body">
              {maturityAnalysis ? (
                (() => {
                  const totalQuestions = maturityAnalysis.dimensionScores.reduce((acc, d) => acc + d.questionCount, 0);
                  const totalAnswered = maturityAnalysis.dimensionScores.reduce((acc, d) => acc + d.answeredCount, 0);
                  const percent = totalQuestions > 0 ? Math.round((totalAnswered / totalQuestions) * 100) : 0;

                  return (
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">
                        {totalAnswered} / {totalQuestions}
                      </div>
                      <div className="text-sm text-gray-500 mb-2">Questions Answered</div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percent}%` }}
                        ></div>
                      </div>
                      <div className="mt-2 text-xs text-gray-400 text-right">{percent}% Complete</div>
                    </div>
                  );
                })()
              ) : (
                <div className="flex justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Assessment Notes */}
      <div className="mt-8" data-notes-section>
        <AssessmentNotes assessmentId={assessmentId} />
      </div>

    </div>
  );
};

export default AssessmentDetail;
